// src/components/BudgetAnalytics.tsx
import { useMemo } from 'react';
import { Paper, Text, Stack, Group, ThemeIcon, Divider } from '@mantine/core';
import {
	IconCalendarDue,
	IconCheck,
	IconClock,
	IconRepeat,
	IconReceipt,
	IconTrendingUp,
	IconTrendingDown,
	IconMinus,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Bill } from '../types';

interface BudgetAnalyticsProps {
	bills: Bill[];
	pastDueBills: Bill[];
}

function BudgetAnalytics({ bills, pastDueBills }: BudgetAnalyticsProps) {
	// Calculate monthly summary
	const monthlySummary = useMemo(() => {
		const now = dayjs();
		const currentMonth = now.month();
		const currentYear = now.year();

		// Filter bills due this month
		const thisMonthBills = bills.filter((bill) => {
			if (!bill.dueDate) return true; // Bills without due date count as current
			const dueDate = dayjs(bill.dueDate);
			return dueDate.month() === currentMonth && dueDate.year() === currentYear;
		});

		const totalDue = thisMonthBills.reduce((sum, bill) => sum + bill.amount, 0);
		const paidAmount = thisMonthBills
			.filter((bill) => bill.paid)
			.reduce((sum, bill) => sum + bill.amount, 0);
		const stillOwed = totalDue - paidAmount;
		const paidCount = thisMonthBills.filter((bill) => bill.paid).length;
		const totalCount = thisMonthBills.length;

		return {
			totalDue,
			paidAmount,
			stillOwed,
			paidCount,
			totalCount,
		};
	}, [bills]);

	// Calculate month-over-month comparison
	const monthComparison = useMemo(() => {
		const now = dayjs();
		const currentMonth = now.month();
		const currentYear = now.year();
		const lastMonth = now.subtract(1, 'month');
		const lastMonthNum = lastMonth.month();
		const lastMonthYear = lastMonth.year();

		// Current month total (including bills without due dates)
		const currentMonthTotal = bills
			.filter((bill) => {
				if (!bill.dueDate) return true;
				const dueDate = dayjs(bill.dueDate);
				return dueDate.month() === currentMonth && dueDate.year() === currentYear;
			})
			.reduce((sum, bill) => sum + bill.amount, 0);

		// Last month total from past due bills that were moved
		const lastMonthTotal = pastDueBills
			.filter((bill) => {
				if (!bill.dueDate) return false;
				const dueDate = dayjs(bill.dueDate);
				return dueDate.month() === lastMonthNum && dueDate.year() === lastMonthYear;
			})
			.reduce((sum, bill) => sum + bill.amount, 0);

		const difference = currentMonthTotal - lastMonthTotal;
		const hasLastMonthData = lastMonthTotal > 0;

		return {
			currentMonthTotal,
			lastMonthTotal,
			difference,
			hasLastMonthData,
		};
	}, [bills, pastDueBills]);

	// Calculate recurring vs one-time breakdown
	const spendingBreakdown = useMemo(() => {
		const recurringBills = bills.filter((bill) => bill.recurring);
		const oneTimeBills = bills.filter((bill) => !bill.recurring);

		const recurringTotal = recurringBills.reduce((sum, bill) => sum + bill.amount, 0);
		const oneTimeTotal = oneTimeBills.reduce((sum, bill) => sum + bill.amount, 0);

		return {
			recurringTotal,
			recurringCount: recurringBills.length,
			oneTimeTotal,
			oneTimeCount: oneTimeBills.length,
		};
	}, [bills]);

	const formatCurrency = (amount: number) => {
		return `$${amount.toFixed(2)}`;
	};

	const getComparisonIcon = () => {
		if (!monthComparison.hasLastMonthData) return IconMinus;
		if (monthComparison.difference > 0) return IconTrendingUp;
		if (monthComparison.difference < 0) return IconTrendingDown;
		return IconMinus;
	};

	const getComparisonColor = () => {
		if (!monthComparison.hasLastMonthData) return 'gray';
		if (monthComparison.difference > 0) return 'red';
		if (monthComparison.difference < 0) return 'green';
		return 'gray';
	};

	const getComparisonText = () => {
		if (!monthComparison.hasLastMonthData) {
			return 'No data from last month to compare';
		}
		if (monthComparison.difference > 0) {
			return `${formatCurrency(monthComparison.difference)} more than last month`;
		}
		if (monthComparison.difference < 0) {
			return `${formatCurrency(Math.abs(monthComparison.difference))} less than last month`;
		}
		return 'Same as last month';
	};

	const ComparisonIcon = getComparisonIcon();

	return (
		<Stack gap="md">
			{/* Monthly Summary Card */}
			<Paper withBorder p="md" radius="md">
				<Group gap="xs" mb="md">
					<ThemeIcon size="md" variant="light" color="blue">
						<IconCalendarDue size={16} />
					</ThemeIcon>
					<Text fw={600}>This Month</Text>
				</Group>

				<Stack gap="sm">
					<Group justify="space-between">
						<Group gap="xs">
							<IconReceipt size={16} color="var(--mantine-color-dimmed)" />
							<Text size="sm" c="dimmed">Total due</Text>
						</Group>
						<Text fw={600} size="lg">{formatCurrency(monthlySummary.totalDue)}</Text>
					</Group>

					<Group justify="space-between">
						<Group gap="xs">
							<IconCheck size={16} color="var(--mantine-color-teal-6)" />
							<Text size="sm" c="dimmed">Paid so far</Text>
						</Group>
						<Text fw={500} c="teal">
							{formatCurrency(monthlySummary.paidAmount)}
							<Text span size="xs" c="dimmed" ml={4}>
								({monthlySummary.paidCount} of {monthlySummary.totalCount})
							</Text>
						</Text>
					</Group>

					<Group justify="space-between">
						<Group gap="xs">
							<IconClock size={16} color="var(--mantine-color-orange-6)" />
							<Text size="sm" c="dimmed">Still owed</Text>
						</Group>
						<Text fw={600} c={monthlySummary.stillOwed > 0 ? 'orange' : 'teal'}>
							{formatCurrency(monthlySummary.stillOwed)}
						</Text>
					</Group>
				</Stack>
			</Paper>

			{/* Month-over-Month Comparison */}
			<Paper withBorder p="md" radius="md">
				<Group gap="xs" mb="sm">
					<ThemeIcon size="md" variant="light" color={getComparisonColor()}>
						<ComparisonIcon size={16} />
					</ThemeIcon>
					<Text fw={600}>vs Last Month</Text>
				</Group>

				<Text size="sm" c={getComparisonColor()}>
					{getComparisonText()}
				</Text>
			</Paper>

			{/* Recurring vs One-time Breakdown */}
			<Paper withBorder p="md" radius="md">
				<Group gap="xs" mb="md">
					<ThemeIcon size="md" variant="light" color="violet">
						<IconRepeat size={16} />
					</ThemeIcon>
					<Text fw={600}>Bill Breakdown</Text>
				</Group>

				<Stack gap="sm">
					<Group justify="space-between">
						<Group gap="xs">
							<IconRepeat size={16} color="var(--mantine-color-violet-6)" />
							<Text size="sm" c="dimmed">Recurring</Text>
						</Group>
						<Text fw={500}>
							{formatCurrency(spendingBreakdown.recurringTotal)}
							<Text span size="xs" c="dimmed" ml={4}>
								({spendingBreakdown.recurringCount} bills)
							</Text>
						</Text>
					</Group>

					<Divider />

					<Group justify="space-between">
						<Group gap="xs">
							<IconReceipt size={16} color="var(--mantine-color-gray-6)" />
							<Text size="sm" c="dimmed">One-time</Text>
						</Group>
						<Text fw={500}>
							{formatCurrency(spendingBreakdown.oneTimeTotal)}
							<Text span size="xs" c="dimmed" ml={4}>
								({spendingBreakdown.oneTimeCount} bills)
							</Text>
						</Text>
					</Group>
				</Stack>
			</Paper>
		</Stack>
	);
}

export default BudgetAnalytics;
